/**
 * @fileoverview Composable for webview functionality
 * @module composables/useWebview
 *
 * Manages webview URL, visibility, and tab navigation
 * for embedded content in the chat window.
 *
 * @vue3migration - Phase 3F: Extracted from ChatWindow/Index.vue
 */
import { computed, type ComputedRef } from 'vue'
import * as _ from 'lodash-es'

/**
 * Tab configuration interface
 */
interface Tab {
  url: string
  name?: string
  style?: string | Record<string, string>
}

/**
 * Store interface for webview operations
 */
export interface WebviewStore {
  webviewUrl: string
  showWebview: boolean
  returnToChatButton: boolean
  setWebviewUrl: (url: string) => void
  setShowWebview: (show: boolean) => void
  setReturnToChat: (show: boolean) => void
  closeWebview: () => void
}

/**
 * Options for useWebview composable
 */
interface UseWebviewOptions {
  /** Pinia store instance */
  store: WebviewStore
  /** Settings containing tabs configuration */
  settings: ComputedRef<{ tabs?: Tab[] }>
}

/**
 * Composable for webview functionality
 *
 * @param options - Dependencies and configuration
 * @returns Webview state and control methods
 *
 * @example
 * ```ts
 * const {
 *   webviewUrl,
 *   showWebview,
 *   iframeWebviewUrl,
 *   returnToChatButton,
 *   handleTabClick,
 *   closeWebView,
 * } = useWebview({
 *   store: useMainStore(),
 *   settings: computed(() => store.settings),
 * })
 * ```
 */
export function useWebview(options: UseWebviewOptions) {
  const { store, settings } = options

  // ====================
  // Computed
  // ====================

  /**
   * Current webview URL from store
   */
  const webviewUrl = computed(() => store.webviewUrl)

  /**
   * Whether webview is currently shown
   */
  const showWebview = computed(() => store.showWebview)

  /**
   * Whether return to chat button is shown
   */
  const returnToChatButton = computed(() => store.returnToChatButton)

  /**
   * Processed iframe URL for webview
   * Handles PDF viewing on Safari mobile devices
   */
  const iframeWebviewUrl = computed(() => {
    const url = webviewUrl.value
    const isPdf = _.includes(url, 'pdf=1')
    const userAgent = _.get(window, 'navigator.userAgent', '')
    const isSafariMobile = userAgent.match(/iPad/i) || userAgent.match(/iPhone/i)

    return isPdf && isSafariMobile
      ? `https://drive.google.com/viewerng/viewer?embedded=true&url=${url}`
      : url
  })

  // ====================
  // Methods
  // ====================

  /**
   * Handle tab click for navigation
   * Switches between main chat and webview tabs
   *
   * @param clickedTab - Tab that was clicked
   * @param iframeRef - Reference to iframe element for style updates
   */
  function handleTabClick(
    clickedTab: { name: string },
    iframeRef?: HTMLIFrameElement | null
  ) {
    if (clickedTab.name === 'main') {
      store.setShowWebview(false)
    } else {
      store.setShowWebview(true)
      if (clickedTab.name !== store.webviewUrl) {
        store.setWebviewUrl(clickedTab.name)
        const tab = _.find(settings.value.tabs, (t) => t.url === clickedTab.name)
        if (tab && iframeRef) {
          const style = _.get(tab, 'style')
          if (style) {
            iframeRef.style.cssText = style
          } else {
            iframeRef.style.cssText = 'width:100%;height:100%'
          }
        }
      }
    }
  }

  /**
   * Close webview and return to chat
   * Resets webview state and scrolls to bottom
   */
  function closeWebView() {
    store.closeWebview()
    store.setShowWebview(false)
    store.setReturnToChat(false)
    // Note: scrollToBottom should be called by component after this
  }

  /**
   * Open webview with a URL
   *
   * @param url - URL to open in webview
   */
  function openWebview(url: string) {
    store.setWebviewUrl(url)
    store.setShowWebview(true)
  }

  return {
    // Computed
    webviewUrl,
    showWebview,
    returnToChatButton,
    iframeWebviewUrl,

    // Methods
    handleTabClick,
    closeWebView,
    openWebview,
  }
}
